import React, { useEffect, useMemo, useState } from 'react'
import ImageGallery from 'react-image-gallery'
import {
  CustomSection,
  Container,
  GalleryModal,
  PreviewGrid,
  PreviewItem,
  Slice
} from './styles'

const buildImagePath = (fileName) => `/imgs/cv_images/${encodeURIComponent(fileName)}`

const featuredProjects = [
  {
    key: 'baby-routine',
    title: 'Baby Routine',
    link: 'https://baby-routine-app.web.app/',
    linkLabel: 'View Baby Routine Project',
    description: 'Application designed to help parents track baby routines, health data and vaccination schedules.',
    features: [
      'feeding tracking',
      'sleep monitoring',
      'vaccine validation',
      'reminders for delayed vaccines',
      'pediatric consultation support'
    ],
    stack: ['Svelte', 'Firebase'],
    images: [
      'baby_routine (1).png',
      'baby_routine (2).png',
      'baby_routine (3).png',
      'baby_routine (4).png',
      'baby_routine (5).png'
    ]
  },
  {
    key: 'bolao-copas',
    title: 'Bolão de Copas (Football Prediction Pool Web App)',
    link: 'https://bolao-de-copas.web.app/',
    linkLabel: 'View Bolão de Copas Project',
    description: 'Application that allows users to create prediction pools for football championships.',
    features: [
      'match predictions',
      'ranking system',
      'multiple competitions',
      'real-time score updates'
    ],
    stack: ['Svelte (current version)', 'Firebase'],
    images: [
      'bolao_copas (1).png',
      'bolao_copas (2).png',
      'bolao_copas (3).png',
      'bolao_copas (4).png'
    ]
  },
  {
    key: 'budget-comparison-tool',
    title: 'Budget Comparison Tool (personal project)',
    link: 'https://orcamento.ivanoliveira.com.br/',
    linkLabel: 'View Budget Comparison Tool Project',
    description: 'Tool designed to manage multiple supplier quotes when planning purchases or services.',
    features: [
      'compare multiple quotes',
      'favorite best prices',
      'contact suppliers',
      'notes and observations'
    ],
    stack: ['React', 'Firebase'],
    images: [
      'orcamento (1).png',
      'orcamento (2).png',
      'orcamento (3).png'
    ]
  }
]


const heroSection = (
  <>
    <h1>Ivan Oliveira</h1>
    <h3>Frontend Developer</h3>

    <p>10+ years of experience building scalable web applications, enterprise e-commerce platforms and modern front-end systems using React, Svelte, Vue, and SAP Commerce (Hybris).</p>
    <p>Based in Brazil • Open to Remote Opportunities</p>

    <a href="https://www.linkedin.com/in/ivanoliveiranet/" target="_blank" rel="noopener noreferrer"><i className="fab fa-linkedin"></i> LinkedIn</a>
    <a href="https://github.com/ivanqvdoliveira" target="_blank" rel="noopener noreferrer"><i className="fab fa-github"></i> GitHub</a>
    <a href="mailto:ivanaugoli@hotmail.com"><i className="fas fa-envelope"></i> ivanaugoli@hotmail.com</a>
  </>
)

const techStackSection = (
  <>
    <h2>Tech Stack</h2>
    <div className='techStackUL'>
      <div>
        <h3>Front End</h3>
        <ul>
          <li>React</li>
          <li>Svelte</li>
          <li>VueJs</li>
          <li>JavaScript</li>
          <li>TypeScript</li>
          <li>HTML/CSS</li>
        </ul>
      </div>

      <div>
        <h3>Back End</h3>
        <ul>
          <li>Node.js</li>
          <li>REST APIs</li>
        </ul>
      </div>

      <div>
        <h3>Tools</h3>
        <ul>
          <li>Git</li>
          <li>Webpack</li>
          <li>Docker</li>
          <li>Firebase</li>
          <li>Vite</li>
          <li>NextJs</li>
        </ul>
      </div>

      <div>
        <h3>Platforms</h3>
        <ul>
          <li>SAP Commerce (Hybris)</li>
        </ul>
      </div>
    </div>
  </>
)


const experienceSection = (
  <>
    <h2>Experience</h2>
    <h3>Frontend Developer</h3>
    <p>Experience building enterprise e-commerce solutions and internal
      tools for large scale platforms with thousands of users.</p>
    <p>Focus on frontend architecture, integrations and performance.</p>
    <p>Main technologies:</p>
    <ul>
      <li>React</li>
      <li>Svelte</li>
      <li>Vuejs</li>
      <li>SAP Commerce (Hybris)</li>
      <li>Jira</li>
      <li>Github</li>
      <li>Design Systems</li>
    </ul>
  </>
)

const contactSection = (
  <>
    <h3>Contact</h3>
    <a href="mailto:ivanaugoli@hotmail.com"><i className="fas fa-envelope"></i> ivanaugoli@hotmail.com</a>
    <a href="https://www.linkedin.com/in/ivanoliveiranet/" target="_blank" rel="noopener noreferrer"><i className="fab fa-linkedin"></i> LinkedIn</a>
    <a href="https://github.com/ivanqvdoliveira" target="_blank" rel="noopener noreferrer"><i className="fab fa-github"></i> GitHub</a>
  </>
)

const HomeCV = () => {
  const [openGalleryProject, setOpenGalleryProject] = useState(null)
  const [openGalleryIndex, setOpenGalleryIndex] = useState(0)

  const galleryItems = useMemo(() => {
    const activeProject = featuredProjects.find(({ key }) => key === openGalleryProject)

    if (!activeProject) {
      return []
    }

    return activeProject.images.map((fileName) => {
      const path = buildImagePath(fileName)

      return {
        original: path,
        thumbnail: path
      }
    })
  }, [openGalleryProject])

  useEffect(() => {
    if (!openGalleryProject) {
      return undefined
    }

    document.body.style.overflow = 'hidden'

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpenGalleryProject(null)
      }
    }

    window.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = 'initial'
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [openGalleryProject])

  const openGallery = (projectKey, index) => {
    setOpenGalleryProject(projectKey)
    setOpenGalleryIndex(index)
  }

  const renderProjectGalleryPreview = (project) => {
    const hasMoreThanThreeImages = project.images.length > 3
    const previewImages = hasMoreThanThreeImages ? project.images.slice(0, 3) : project.images

    if (!previewImages.length) {
      return null
    }

    return (
      <PreviewGrid>
        {previewImages.map((image, index) => {
          const hiddenImagesCount = project.images.length - 2

          return (
            <PreviewItem
              key={`${project.key}-${image}`}
              type='button'
              onClick={() => openGallery(project.key, index)}
              aria-label={`Open ${project.title} image gallery`}
            >
              <img src={buildImagePath(image)} alt={`${project.title} screenshot ${index + 1}`} loading='lazy' />
              {hasMoreThanThreeImages && index === 2 && (
                <span className='counter'>+{hiddenImagesCount}</span>
              )}
            </PreviewItem>
          )
        })}
      </PreviewGrid>
    )
  }

  const featuredProjectsSection = (
    <>
      <h2>Featured Projects</h2>

      {featuredProjects.map((project) => (
        <div key={project.key}>
          <h3>{project.title}</h3>
          <p>{project.description}</p>
          {renderProjectGalleryPreview(project)}

          <h3>Features</h3>
          <ul>
            {project.features.map((feature) => (
              <li key={`${project.key}-${feature}`}>{feature}</li>
            ))}
          </ul>

          <h3>Tech Stack</h3>
          <ul>
            {project.stack.map((technology) => (
              <li key={`${project.key}-${technology}`}>{technology}</li>
            ))}
          </ul>
          <a href={project.link} target="_blank" rel="noopener noreferrer" className="view"><i className="fas fa-external-link-alt"></i> {project.linkLabel}</a>
        </div>
      ))}

      <h2>Other Work</h2>
      <p>Various landing pages and static websites developed for
        personal and experimental projects.</p>
    </>
  )

  return (
    <CustomSection>
      {openGalleryProject && (
        <GalleryModal>
          <button
            type='button'
            onClick={() => setOpenGalleryProject(null)}
            className='closeGalleryButton'
            aria-label='Close image gallery'
          >
            <i className="fas fa-times" />
          </button>
          <div className='galleryContent'>
            <ImageGallery
              items={galleryItems}
              startIndex={openGalleryIndex}
              showPlayButton={false}
              showFullscreenButton={false}
              showBullets
            />
          </div>
        </GalleryModal>
      )}
      <article>
        <Container>{heroSection}</Container>
        <Slice />
        <Container>{techStackSection}</Container>
        <Slice />
        <Container>{featuredProjectsSection}</Container>
        <Slice />
        <Container>{experienceSection}</Container>
        <Slice />
        <Container>{contactSection}</Container>
      </article>
    </CustomSection>
  )
}

export default HomeCV
